// Dedicated entry for the public marketing site (/fidava). Deliberately
// minimal: it must NOT import the app store or the full routes —
// that's the whole point (keep the landing page off the heavy app bundle).
// Only what the marketing pages need: i18n, MUI (App self-themes), framer, and
// react-router for the two marketing routes.
import "./appConfig";
import "@Styles/main.scss";
import "./fonts";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { Router } from "react-router";
import { Route, Switch } from "react-router-dom";
import { createBrowserHistory } from "history";
import { I18nextProvider, initReactI18next } from "react-i18next";
import i18next from "i18next";
import App from "@Pages/site/App";
import { determineInitialLocale } from "@Pages/site/SiteContentContext";
import { ensureLocaleContent, findSupportedLocale } from "@Pages/site/content";

// The printable brochure is its own route — kept out of the landing page's
// initial JavaScript.
const BrochurePage = React.lazy(() => import(/* webpackChunkName: "site-brochure" */ "@Pages/site/BrochurePage"));
import { installDeclarativeClickTracking } from "./core/analytics/ga4";

// GA4: any element tagged data-ga-event="…" reports a click (see core/analytics/ga4).
installDeclarativeClickTracking();
// Cookie-consent banner + preferences dialog (GDPR). The marketing site
// is where the tracking cookies (GA) actually load, so it needs the
// banner + the footer's "Cookie settings" re-open link. These small
// JSONs are the ONLY i18n namespace the landing page pulls — far from
// the heavy ~217-namespace app bundle this entry deliberately avoids.
import ccEn from "@Components/consent/resources/en/CookieConsent.json";
import ccPt from "@Components/consent/resources/pt/CookieConsent.json";
import ccEs from "@Components/consent/resources/es/CookieConsent.json";
import ccFr from "@Components/consent/resources/fr/CookieConsent.json";
import ccDe from "@Components/consent/resources/de/CookieConsent.json";
import ccIt from "@Components/consent/resources/it/CookieConsent.json";
// Theme picker labels (the site offers the app's theme catalogue).
import tpEn from "@Components/theme/resources/en/ThemePicker.json";
import tpPt from "@Components/theme/resources/pt/ThemePicker.json";
import tpEs from "@Components/theme/resources/es/ThemePicker.json";
import tpFr from "@Components/theme/resources/fr/ThemePicker.json";
import tpDe from "@Components/theme/resources/de/ThemePicker.json";
import tpIt from "@Components/theme/resources/it/ThemePicker.json";

// Brand font (Montserrat) is self-hosted and bundled via `./fonts` (imported
// above) — no runtime <link> injection or CDN dependency needed.

// Language comes from the i18next-persisted key (written by the app and by
// SiteLanguageMenu), default pt — except that a ?lang= in the URL wins. Those
// URLs are the page's language alternates (hreflang), which the server also
// renders in that language; before this, /fidava?lang=en showed Portuguese
// to any new visitor (and to search engines) because the stored/default "pt"
// was passed down as the explicit locale.
let initialLanguage = "pt";
try { initialLanguage = window.localStorage.getItem("i18nextLng") || "pt"; } catch { /* ignore */ }
const urlLanguage = findSupportedLocale(new URLSearchParams(window.location.search).get("lang") ?? undefined);
if (urlLanguage) { initialLanguage = urlLanguage; }

// Catch-all: any non-marketing path reached via client-side navigation (e.g.
// a CTA to /startupWizard or /login) must hand off to the full app bundle —
// the marketing entry can't render app routes. A hard navigation re-requests
// Index.cshtml, which serves the main-client entry for non-/fidava paths.
const ExternalRedirect: React.FC<{ location?: { pathname?: string; search?: string } }> = ({ location }) => {
    React.useEffect(() => {
        const target = (location?.pathname || "/") + (location?.search || "");
        window.location.replace(target);
    }, [location]);
    return null;
};

const history = createBrowserHistory();

// The marketing site renders ALL its copy from SiteContentContext (site.json),
// not i18next namespaces — there are zero t('…') calls. So init i18n with empty
// resources purely to provide/track the current language, and do NOT pull the
// app's full ~217-namespace translation bundle. That bundle (the multi-MB
// i18n-<lng> chunk) was the single biggest cost on /fidava — ~2 s of CPU on
// the LCP path — for strings the landing page never uses.
const i18nReady = i18next.use(initReactI18next).init({
    lng: initialLanguage,
    fallbackLng: "en",
    // Only the CookieConsent and ThemePicker namespaces — see the import note above. The
    // rest of the landing copy comes from SiteContentContext, not i18next.
    resources: {
        en: { CookieConsent: ccEn, ThemePicker: tpEn },
        pt: { CookieConsent: ccPt, ThemePicker: tpPt },
        es: { CookieConsent: ccEs, ThemePicker: tpEs },
        fr: { CookieConsent: ccFr, ThemePicker: tpFr },
        de: { CookieConsent: ccDe, ThemePicker: tpDe },
        it: { CookieConsent: ccIt, ThemePicker: tpIt },
    },
    interpolation: { escapeValue: false },
    partialBundledLanguages: true,
    react: { useSuspense: false },
});

// The landing copy for the visitor's language is a lazy chunk (content.ts);
// fetch it alongside i18next so the first render is already in the right
// language — the #preloader splash covers the wait. Both the explicit
// ?lang/stored choice and the i18next language are warmed.
const contentReady = Promise.all(
    [determineInitialLocale(initialLanguage), findSupportedLocale(initialLanguage)]
        .filter((l): l is NonNullable<typeof l> => Boolean(l))
        .map((l) => ensureLocaleContent(l))
);

Promise.all([i18nReady.catch(() => undefined), contentReady]).finally(() => {
    const el = document.getElementById("react-app");
    if (!el) return;
    // No SnackbarProvider: the marketing pages render zero snackbars (no
    // useSnackbar / enqueueSnackbar anywhere under pages/site), so providing it
    // only dragged notistack into the marketing-vendors bundle for nothing.
    const routing = (
        <Switch>
            <Route exact path="/fidava/brochure" render={() => (
                <React.Suspense fallback={null}><BrochurePage /></React.Suspense>
            )} />
            <Route exact path={["/fidava", "/converge", "/Converge", "/"]} component={App} />
            <Route path="*" component={ExternalRedirect} />
        </Switch>
    );
    createRoot(el).render(
        <I18nextProvider i18n={i18next} defaultNS={"pt"}>
            <Router history={history} children={routing} />
        </I18nextProvider>
    );
    // Dismiss the branded splash (#preloader) once the site has mounted. The
    // app does this in boot-client; the marketing entry must do it too, or the
    // full-screen white preloader overlay (inline-styled in Index.cshtml, shown
    // by default) is never removed and covers the /fidava page.
    const preloader = document.getElementById("preloader");
    if (preloader) preloader.style.display = "none";
});
